export const heroChecklist = [
  'Articulation activities organized by speech sounds and practice levels.',
  'Receptive language exercises for directions, vocabulary, and concepts.',
  'Expressive language activities for sentences, descriptions, and storytelling.',
  'Practical social and functional communication activities.',
  'Printable minimal pairs therapy cards.',
  'Interactive speech therapy games.',
  'Home practice activities for families.',
  'Professional data collection and progress tracking forms.',
];

export const problemCards = [
  {
    variant: 'blue' as const,
    title: 'WHAT IF YOUR SPEECH THERAPY ACTIVITIES WERE ALREADY PREPARED?',
    paragraphs: [
      "As a speech-language professional, you know how important it is to choose activities that match each child's individual goals.",
      'But preparing therapy materials can take time. You may need to find pictures, organize exercises, create word lists, and search for new activities before each session.',
      'What can you use to target a specific speech sound? How can you work on following directions? Which activities can support sentence development? What can you use for social communication goals?',
      'Instead of relying on scattered resources, the Complete Speech Therapy Activities Bundle™ brings practical materials together across four essential areas of pediatric speech-language intervention.',
    ],
    highlight: 'ORGANIZE. SELECT. APPLY. SUPPORT.',
  },
  {
    variant: 'gold' as const,
    title: 'LESS TIME PREPARING MATERIALS FOR EVERY SESSION.',
    paragraphs: [
      "Your time is valuable. It should be focused on understanding your clients' needs, selecting appropriate therapy goals, and supporting their progress.",
      'Constantly preparing word lists, searching for pictures, and building new activities can take up a significant part of your schedule.',
      'The Complete Speech Therapy Activities Bundle™ brings articulation, receptive language, expressive language, and social communication resources together in one organized collection.',
      "Choose the area you need, select an activity, and adapt it to the child's individual needs.",
    ],
    highlight: 'LESS REPETITIVE PREPARATION. MORE ORGANIZED THERAPY RESOURCES.',
  },
  {
    variant: 'navy' as const,
    title: 'LESS SEARCHING. MORE READY-TO-USE ACTIVITIES.',
    paragraphs: [
      "Need activities for specific speech sounds? They're organized by target.",
      "Working on following directions, concepts, or vocabulary? You'll find structured receptive language exercises.",
      'Supporting sentence development, descriptions, storytelling, or functional communication? There are practical activities for those goals, too.',
      "You'll also have minimal pairs cards, therapy games, home practice resources, and professional tracking forms.",
    ],
    highlight: 'OPEN THE MATERIAL. CHOOSE THE ACTIVITY. ADAPT IT. USE IT.',
  },
];

export const volumes = [
  {
    number: '01',
    title: 'ARTICULATION PRACTICE ACTIVITIES',
    points: [
      'Work on speech sound production with structured activities organized around individual therapy targets.',
      'Use exercises targeting consonants, sounds in initial, medial, and final word positions, consonant clusters, and different practice levels.',
      'Explore word-level, phrase-level, and sentence-level activities to support movement from structured practice toward broader communication contexts.',
      "Choose appropriate stimuli, adjust cueing, and organize practice according to each child's needs.",
    ],
    finalLine: 'SELECT. PRACTICE. PROGRESS.',
  },
  {
    number: '02',
    title: 'RECEPTIVE LANGUAGE ACTIVITIES',
    points: [
      'Access practical exercises targeting the understanding of words, directions, concepts, and spoken messages.',
      'Use activities involving object and action identification, functions, attributes, categories, and relationships between items.',
      'Work on one-step, two-step, and three-step directions, spatial concepts, temporal concepts, quantity, comparisons, and sentence comprehension.',
      'Includes sequencing, short stories, cause-and-effect relationships, and simple inference activities that can be adapted to individual needs.',
    ],
    finalLine: 'LISTEN. IDENTIFY. UNDERSTAND. RESPOND.',
  },
  {
    number: '03',
    title: 'EXPRESSIVE LANGUAGE ACTIVITIES',
    points: [
      'Organize sessions with practical activities for vocabulary, sentence development, grammar, and storytelling.',
      'Use naming, object description, action description, comparison, word retrieval, and sentence expansion exercises.',
      'Explore activities targeting verb forms, pronouns, plurals, connectors, and different sentence structures.',
      'Includes picture-based activities and sequences for descriptions, event organization, and narrative development.',
    ],
    finalLine: 'NAME. DESCRIBE. BUILD. EXPRESS.',
  },
  {
    number: '04',
    title: 'SOCIAL COMMUNICATION ACTIVITIES',
    points: [
      'Support functional communication through practical activities based on everyday situations.',
      'Use exercises involving conversation initiation, asking questions, sharing information, requesting help, and participating in interactions.',
      'Explore illustrated scenarios for clarification, communication breakdowns, expressing preferences, communicating needs, and shared decision-making.',
      'Includes school, family, and community situations that can be adapted to different communication styles and individual needs.',
    ],
    finalLine: 'COMMUNICATE. PARTICIPATE. CLARIFY. CONNECT.',
  },
];

// Replace each `image` with a real PDF preview URL when available.
export const previews = [
  { image: 'https://cdn.magicpatterns.com/uploads/b9Lf2dR2gdH8pm1hjV6T9L/Screenshot_20261014_193833_Drive.jpg', caption: 'TR Sound Practice Path — /tr/ cluster words, phrases, and sentences with tracking.' },
  { image: 'https://cdn.magicpatterns.com/uploads/hy1JpTRbUASJahcruqxytK/Screenshot_20261014_193820_Drive.jpg', caption: 'Start Strong with /ST/ — initial cluster picture cards, sentences, and independent challenge.' },
  { image: 'https://cdn.magicpatterns.com/uploads/q6SmppQUqtq1x8wyEWyos4/Screenshot_20261014_193801_Drive.jpg', caption: 'Practice the /J/ Sound — initial and final position words, phrases, and sentences.' },
  { image: 'https://cdn.magicpatterns.com/uploads/6u8DdcAyvDYAyMQ7spwEFz/Screenshot_20261014_193748_Drive.jpg', caption: 'SH Sound Picture Practice — word, phrase, and sentence-level /ʃ/ activities.' },
  { image: 'https://cdn.magicpatterns.com/uploads/v6JGvVrBo1hp9DrBLG2JXR/Screenshot_20261014_193735_Drive.jpg', caption: 'Follow the /N/ Sound Path — syllable to sentence progression with 20 response spaces.' },
  { image: 'https://cdn.magicpatterns.com/uploads/uXrBnbhgj5vaWe5niWoj5V/Screenshot_20261014_193727_Drive.jpg', caption: 'Build a Phrase with /T/ — picture cards, phrase starters, and 16 planned opportunities.' },
];

export const bonuses = [
  {
    number: '01',
    title: 'MINIMAL PAIRS THERAPY CARDS',
    shortTitle: 'Minimal Pairs Therapy Cards',
    price: 12.9,
    points: [
      'Printable illustrated cards for phonological contrast activities.',
      'Access organized word pairs designed to highlight sound differences that can change word meaning.',
      'Use the cards for auditory identification, picture selection, contrastive production, and meaning-based communication activities.',
      "Choose appropriate contrasts based on each child's individual therapy goals.",
    ],
    finalLine: 'COMPARE. IDENTIFY. PRACTICE. DIFFERENTIATE.',
  },
  {
    number: '02',
    title: 'SPEECH THERAPY GAMES & INTERACTIVE ACTIVITIES',
    shortTitle: 'Speech Therapy Games',
    price: 14.9,
    points: [
      'Printable therapy games designed to bring interactive practice into your sessions.',
      'Access game boards, cards, matching games, memory activities, and structured interactive tasks.',
      'Use the materials to support speech sound practice, vocabulary, comprehension, sentence building, and functional communication.',
      'Select activities based on therapy goals and adapt them for individual sessions or small groups.',
    ],
    finalLine: 'CHOOSE. PLAY. PRACTICE. PARTICIPATE.',
  },
  {
    number: '03',
    title: 'SPEECH THERAPY HOME PRACTICE PACK',
    shortTitle: 'Home Practice Pack',
    price: 12.9,
    points: [
      'Practical activities you can select and share with families to support therapy goals outside sessions.',
      'Access exercises with simple instructions, examples, illustrations, and everyday practice ideas.',
      'Includes vocabulary, following directions, sentence production, storytelling, functional communication, and selected speech sound practice activities.',
      'Help caregivers support the goals identified by the treating professional.',
    ],
    finalLine: 'SELECT. SHARE. PRACTICE. SUPPORT.',
  },
  {
    number: '04',
    title: 'SPEECH THERAPY DATA COLLECTION & PROGRESS TRACKERS',
    shortTitle: 'Data Collection & Progress Trackers',
    price: 14.9,
    points: [
      'Professional tools for organizing session documentation and tracking therapy goals.',
      'Access data sheets, response tracking forms, accuracy tables, observation forms, and progress comparison tools.',
      'Use resources designed for articulation, receptive language, expressive language, and functional communication targets.',
      'Document responses, cueing levels, observations, and progress through organized forms.',
    ],
    finalLine: 'RECORD. COMPARE. REVIEW. PLAN.',
  },
];

export const benefits = [
  { title: 'Activities organized by goal', text: 'Find exercises grouped by therapy area so you can select materials for specific targets.' },
  { title: 'Practical session materials', text: 'Use activities with prompts, pictures, stimuli, and instructions that can be adapted to your clients.' },
  { title: 'Four core areas in one bundle', text: 'Access articulation, receptive language, expressive language, and social communication resources.' },
  { title: 'Reusable printable materials', text: 'View the PDFs, print the activities you need, and reuse them within the terms of your license.' },
  { title: 'Everything in one organized collection', text: 'Keep volumes, printable cards, games, and tracking tools together.' },
  { title: 'Simpler session preparation', text: 'Choose structured activities instead of repeatedly building similar materials from scratch.' },
];

export const offerChecklist = [
  'Volume 01 — Articulation Practice Activities',
  'Volume 02 — Receptive Language Activities',
  'Volume 03 — Expressive Language Activities',
  'Volume 04 — Social Communication Activities',
  'Printable and reusable materials',
  'Activities organized by therapy goals',
];

export const testimonials = [
  {
    name: 'Jessica M.',
    role: 'M.S., CCC-SLP · Elementary school SLP, Ohio',
    age: 34,
    topic: 'Articulation activities',
    quote:
      "My caseload is 52 kids, and most of my articulation students need the same sounds at different levels. Having the word, phrase, and sentence activities already organized by target means I can pull /s/ blends for one group and final /k/ for the next without rebuilding anything. It's become my go-to binder.",
  },
  {
    name: 'Rachel T.',
    role: 'CCC-SLP · Private pediatric practice, Texas',
    age: 41,
    topic: 'Organization & session prep',
    quote:
      "I used to spend Sunday evenings printing pictures and building word lists. Now I open the volume I need, pick an activity, and adapt it in a few minutes. It didn't change how I do therapy — it just gave me back a lot of prep time.",
  },
  {
    name: 'Amanda K.',
    role: 'M.A., CCC-SLP · Outpatient pediatric clinic, Florida',
    age: 29,
    topic: 'Therapy cards & games',
    quote:
      "The minimal pairs cards are clear and easy for my phonology kids to see, and the games keep my 5- and 6-year-olds engaged through a full session. I laminated the game boards and use them with small groups almost every day.",
  },
  {
    name: 'Laura P.',
    role: 'CCC-SLP · Early intervention & school-age, North Carolina',
    age: 47,
    topic: 'Home practice & tracking',
    quote:
      "The home practice pack makes it easy to send parents something simple that actually matches our goals. And the data sheets fit how I already track cueing levels, so documentation is faster and more consistent across my students.",
  },
  {
    name: 'Megan R.',
    role: 'SLP-CF · Public school district, California',
    age: 26,
    topic: 'Language & social communication',
    quote:
      "As a clinical fellow, I appreciated having structured receptive, expressive, and social communication activities in one place. The illustrated scenarios have been great for my students working on asking for help and clarifying messages.",
  },
];

export const faqs = [
  { q: 'Is this a physical product?', a: 'No. The Complete Speech Therapy Activities Bundle™ is a 100% digital PDF collection. No physical products will be shipped.' },
  { q: 'Who is this bundle designed for?', a: 'It is primarily designed for Speech-Language Pathologists, speech therapists, and qualified professionals who work with children and need practical therapy resources.' },
  { q: 'Is this a course or certification?', a: 'No. This is a collection of therapy activities, printable materials, and professional tools. It does not provide certification.' },
  { q: 'Is this another theory-heavy ebook?', a: 'No. The collection focuses on practical exercises, activities, therapy cards, games, and professional forms.' },
  { q: 'Do I need to create the activities myself?', a: "The materials include structured exercises, stimuli, and activity instructions. You can select and adapt them to your clients' individual needs." },
  { q: 'Which therapy areas are covered?', a: 'Articulation, receptive language, expressive language, and social communication.' },
  { q: 'What age group is this designed for?', a: 'The activities are primarily intended for children approximately 4–10 years old, depending on their developmental abilities, individual needs, and therapy goals.' },
  { q: 'Can I use these activities with different clients?', a: 'Yes, within your professional practice and according to the license terms provided with your purchase.' },
  { q: 'Are the materials printable?', a: 'Yes. The materials are designed for digital viewing and printing, depending on the specific activity.' },
  { q: 'Are the four bonuses really free?', a: 'Yes. All four professional bonuses are included at no additional cost when you purchase the main bundle for $26.90 USD.' },
  { q: 'What is included in the free bonuses?', a: 'Minimal pairs therapy cards, interactive speech therapy games, home practice activities, and professional data collection and progress tracking forms.' },
  { q: 'Do these activities replace professional assessment?', a: 'No. These materials are professional support resources. Assessment, goal selection, treatment planning, and activity adaptation remain the responsibility of the treating professional.' },
  { q: 'How will I receive my purchase?', a: "After completing your purchase, you will receive digital access to the files through the store's delivery system." },
];

export const recentBuyers = [
  { name: 'Emily Carter', place: 'Austin, United States', minutes: 12 },
  { name: 'Sophie Tremblay', place: 'Toronto, Canada', minutes: 9 },
  { name: 'Hannah Wright', place: 'Manchester, United Kingdom', minutes: 18 },
  { name: 'Olivia Bennett', place: 'Sydney, Australia', minutes: 7 },
  { name: 'Grace Thompson', place: 'Auckland, New Zealand', minutes: 14 },
  { name: 'Chloe Byrne', place: 'Dublin, Ireland', minutes: 11 },
  { name: 'Ashley Morgan', place: 'Denver, United States', minutes: 16 },
  { name: 'Priya Nair', place: 'Singapore', minutes: 5 },
  { name: 'Lauren Botha', place: 'Cape Town, South Africa', minutes: 21 },
];
